import { toast } from "react-hot-toast";
import {
  Button,
  CodeBlock,
  Collapsible,
  inlineCodeClass,
} from "./components.tsx";
import { transact, tx } from "./db.ts";

export const signedToolRequestType = `type SignedToolRequest<T> = {
  payload: {
    meta: {
      botId: string;
      conversationId: string;
      userId: string;
      network: string;
      timestamp: string; // unix ms as string
      nonce: string;
      toolCallId?: string; // unique ID for this tool invocation
      toolName?: string;   // name of the tool being called
    };
    params: T;
  };
  signature: string; // hex hmac sha256 of JSON.stringify(payload)
};`;

export const verifyHelperCode = (secret: string, extraNote?: string) =>
  `import { createHmac } from "node:crypto";

const secret = "${secret}";

${signedToolRequestType}

const extractSignedParams = <T>(input: SignedToolRequest<T>): T => {
  if (!input || typeof input !== "object") throw new Error("Bad payload");
  const { payload, signature } = input;
  if (!payload || typeof signature !== "string") {
    throw new Error("Bad payload");
  }
  const payloadStr = JSON.stringify(payload);
  const expected = createHmac("sha256", secret).update(payloadStr).digest("hex");
  if (expected !== signature) throw new Error("Bad signature");
  const { meta, params } = payload as SignedToolRequest<T>["payload"];
  const ts = Number(meta?.timestamp);
  if (!Number.isFinite(ts)) throw new Error("Bad timestamp");
  if (Math.abs(Date.now() - ts) > 5 * 60_000) {
    throw new Error("Stale request");
  }${extraNote ? `\n  ${extraNote}` : ""}
  if (params === undefined) throw new Error("Missing params");
  return params as T;
};`;

const updateWebhookSecret = (botId: string, webhookSecret: string) =>
  transact(tx.bots[botId].update({ webhookSecret }));

export const SigningAuthDocs = (
  {
    botId,
    webhookSecret,
    extraNote,
  }: {
    botId: string;
    webhookSecret?: string;
    extraNote?: string;
  },
) => {
  const code = webhookSecret
    ? verifyHelperCode(webhookSecret, extraNote)
    : null;

  return (
    <Collapsible title="Webhook signing">
      <div className="text-sm text-text-secondary-light dark:text-text-secondary space-y-2">
        <div className="flex flex-col gap-2 rounded-lg border border-border-subtle-light dark:border-border-subtle bg-surface-raised-light dark:bg-surface-raised p-3">
          <div className="font-medium text-text-primary-light dark:text-text-primary">
            Webhook secret
          </div>
          {webhookSecret
            ? (
              <>
                <code className={`${inlineCodeClass} break-all`}>
                  {webhookSecret}
                </code>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={async () => {
                      await navigator.clipboard.writeText(webhookSecret);
                      toast.success("Copied!");
                    }}
                    className="px-2 py-1 text-xs"
                  >
                    Copy secret
                  </Button>
                  <Button
                    onClick={() =>
                      toast.promise(
                        updateWebhookSecret(botId, crypto.randomUUID()),
                        {
                          loading: "Rotating webhook secret...",
                          success: "Webhook secret rotated!",
                          error: (err: Error) =>
                            `Failed to rotate webhook secret: ${err.message}`,
                        },
                      )}
                    className="px-2 py-1 text-xs"
                  >
                    Rotate secret
                  </Button>
                </div>
              </>
            )
            : (
              <div className="flex items-center gap-2">
                <div>No webhook secret yet.</div>
                <Button
                  onClick={() =>
                    toast.promise(
                      updateWebhookSecret(botId, crypto.randomUUID()),
                      {
                        loading: "Generating webhook secret...",
                        success: "Webhook secret generated!",
                        error: (err: Error) =>
                          `Failed to generate webhook secret: ${err.message}`,
                      },
                    )}
                  className="px-2 py-1 text-xs"
                >
                  Generate secret
                </Button>
              </div>
            )}
        </div>
        {code
          ? (
            <>
              <div className="flex items-center gap-2">
                <p className="m-0">Copy a ready-made verify helper:</p>
                <Button
                  onClick={async () => {
                    await navigator.clipboard.writeText(code);
                    toast.success("Copied!");
                  }}
                  className="px-2 py-1 text-xs"
                >
                  Copy verify helper (TS/Node)
                </Button>
              </div>
              <CodeBlock code={code} lang="typescript" />
            </>
          )
          : <div>Generate a webhook secret to see the verify helper.</div>}
      </div>
    </Collapsible>
  );
};
